import React from 'react';
import get from 'lodash.get';
import { object, bool, string } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import storage from 'dibs-browser-storage';

import AddCartItemMutation from './AddCartItemMutation';
import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownHeader from '../Dropdown/DropdownHeader';
import DropdownBody from '../Dropdown/DropdownBody';
import CartNavDropdownTrigger from './CartNavDropdownTrigger';
import CartNavDropdownContent from './CartNavDropdownContent';

const CART_ID = 'cart';

class CartNav extends React.Component {

    static getLocalCart() {
        const { localStorage } = storage;
        return localStorage.getItem(CART_ID) || [];
    }

    static deleteLocalCart() {
        const { localStorage } = storage;
        return localStorage.deleteItem(CART_ID);
    }

    constructor(props) {

        super(props);

        this.state = {
            fragmentVariables: {
                fetchLocalItems: false,
                fetchPortfolioItems: false,
                itemIds: "",
                userId: ""
            }
        };

        this.triggerItemsFetch = this.triggerItemsFetch.bind(this);
    }


    componentDidUpdate() {
        const { relay, user, userId } = this.props;
        const localCart = CartNav.getLocalCart();

        if (!!user && localCart.length > 0) {

            AddCartItemMutation.commit({
                environment: relay.environment,
                itemsIds: localCart.map(({ id }) => id),
                userId,
                onCompleted: () => {
                    CartNav.deleteLocalCart();
                },
                onError: (error) => {
                    const { source } = error.getError ? error.getError() : {};

                    if (source) {
                        const message = get(source, 'errors[0].message', '');
                        const isDuplicate = message.includes('409');

                        if (isDuplicate) {
                            CartNav.deleteLocalCart();
                        }
                    }
                }
            });
        }
    }

    /**
     * Inventory SOLR doesn't guarantee returning items in the order itemIds were passed to it
     * Sort response from Inventory SOLR by order of itemIds in local cart
     */
    getSortedLocalItems() {
        const { viewer } = this.props;
        const itemIds = CartNav.getLocalCart().map((id) => id);
        const localItems = get(viewer, 'items');

        if (itemIds.length < 2 || !!localItems) {
            return localItems;
        }

        const map = itemIds.reduce((acc, itemId, index) => {
            acc[itemId] = index;
            return acc;
        }, {});

        const { edges } = localItems;

        edges.sort(({ node: nodeA }, { node: nodeB }) => {
            const { serviceId: a } = nodeA;
            const { serviceId: b } = nodeB;
            return map[a] - map[b];
        });

        return localItems;
    }

    isEmptyState() {
        const { hasUserId } = this.props;

        if (!hasUserId && CartNav.getLocalCart().length === 0) {
            return true;
        }

        const items = this.getItems();
        return !!(items && items.edges && items.edges.length === 0);
    }

    getCurrency() {
        const { hasUserId, user } = this.props;
        const { sessionStorage } = storage;
        const currency = hasUserId ? get(user, 'profile.currency') : sessionStorage.getItem('userCurrency');
        return currency || 'USD';
    }

    getItems() {
        const { hasUserId, viewer } = this.props;

        return hasUserId ?
            get(viewer, 'portfolios.edges[0].node.items') :
            this.getSortedLocalItems();
    }

    getCount() {
        const { hasUserId, viewer } = this.props;

        return hasUserId ?
            (get(viewer, 'portfolios.edges[0].node.items.totalResults') || 0) :
            CartNav.getLocalCart().length;
    }

    triggerItemsFetch() {
        const { hasRootPendingRefetch, hasUserId } = this.props;

        if (hasRootPendingRefetch) {
            return;
        }

        if (hasUserId && !this.state.fragmentVariables.fetchPortfolioItems) {
            this.fetchPortfolioItems();
        }

        if (!hasUserId && !this.state.fragmentVariables.fetchLocalItems) {
            this.fetchLocalItems();
        }
    }

    fetchPortfolioItems() {
        this.setState({
            fragmentVariables: {
                ...this.state.fragmentVariables,
                hasUserId: this.props.hasUserId,
                fetchLocalItems: false,
                fetchPortfolioItems: true,
                userId: this.props.userId
            }

        }, () => {
            this.props.relay.refetch(this.state.fragmentVariables, null);
        });
    }

    fetchLocalItems() {
        const localCart = CartNav.getLocalCart();
        const itemIds = localCart.map(({ id }) => id);

        if (itemIds.length > 0) {
            this.setState({
                fragmentVariables: {
                    ...this.state.fragmentVariables,
                    fetchLocalItems: true,
                    localItemIds: itemIds
                }

            }, () => {
                this.props.relay.refetch(this.state.fragmentVariables, null);
            });
        }
    }

    render() {
        const { isClient } = this.props;

        return isClient ? (
            <Dropdown align={alignConstants.ALIGN_RIGHT}>
                <CartNavDropdownTrigger count={this.getCount()} handleMouseEnter={this.triggerItemsFetch} />
                <DropdownHeader
                    title={<FormattedMessage id='dbl.Header.CartNav.header_title' defaultMessage={'My Cart'}/>}
                    link={<FormattedMessage id='dbl.Header.CartNav.header_link' defaultMessage={'View All'}/>}
                    href='/my/cart/'
                />
                <DropdownBody>
                    <CartNavDropdownContent
                        currency={this.getCurrency()}
                        isEmptyState={this.isEmptyState()}
                        items={this.getItems()}
                    />
                </DropdownBody>
            </Dropdown>
        ) : (
            <CartNavDropdownTrigger />
        );
    }
}

CartNav.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isClient: bool,
    relay: object,
    user: object,
    userId: string,
    viewer: object
};

/* eslint-disable no-unused-expressions */
graphql`
    fragment CartNav_financialAmount on FinancialAmount {
        amount
        currency
        convertedAmountList {
            currency
            amount
        }
    }
`;

graphql`
    fragment CartNav_item on Item {
        pdpURL
        serviceId
        title
        isSold
        photos {
            versions {
                webPath
                type {
                    name
                }
            }
        }
        pricing {
            netPrice {
                ...CartNav_financialAmount @relay(mask: false)
            }
            salePrice {
                ...CartNav_financialAmount @relay(mask: false)
            }
            retailPrice {
                ...CartNav_financialAmount @relay(mask: false)
            }
        }
    }
`;
/* eslint-enable no-unused-expressions */

export default createRefetchContainer(
    CartNav, {
        user: graphql`
            fragment CartNav_user on User {
                profile {
                    currency
                }
            }
        `,
        viewer: graphql.experimental`
            fragment CartNav_viewer on Viewer
            @argumentDefinitions(
                fetchPortfolioItems: {type: "Boolean!", defaultValue: false}
                fetchLocalItems: {type: "Boolean!", defaultValue: false}
                localItemIds: {type: "[String]", defaultValue: []}
            )
            {
                items(itemIds: $localItemIds, first: 3) @include (if: $fetchLocalItems) {
                    edges {
                        node {
                            ...CartNav_item @relay(mask: false)
                        }
                    }
                }
                portfolios(userIds: $userIds, portfolioTypes: [CART], first: 1) @include(if: $hasUserId) {
                    edges {
                        node {
                            items(first: 3) {
                                totalResults
                                edges @include(if: $fetchPortfolioItems) {
                                    node {
                                        item {
                                            ...CartNav_item @relay(mask: false)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `
    },
    graphql.experimental`
        query CartNavRefetchQuery(
            $fetchLocalItems: Boolean!
            $fetchPortfolioItems: Boolean!
            $hasUserId: Boolean!
            $localItemIds: [String]
            $userIds: [String!]
        ) {
            viewer {
                ...CartNav_viewer @arguments(
                    fetchLocalItems: $fetchLocalItems
                    fetchPortfolioItems: $fetchPortfolioItems
                    localItemIds: $localItemIds
                )
            }
        }
    `
);



// WEBPACK FOOTER //
// ./src/components/CartNav/CartNav.jsx